<template>
  <input
    v-if="type !== 'textarea'"
    v-bind="sharedProps"
    :id="resolvedId"
    ref="input"
    v-model="model"
    :type="resolvedType"
    :inputmode="resolvedInputmode"
    :class="[
      'input text-base initial:w-full',
      {
        'input-primary bg-base-200 text-base-content': theme === 'default',
        'input-lg': size === 'lg',
        'input-md': size === 'md',
        'input-sm': size === 'sm',
        'input-xs': size === 'xs',
      },
    ]"
    @input="validateInput"
    @blur="handleBlur"
  />
  <textarea
    v-else
    v-bind="sharedProps"
    :id="resolvedId"
    ref="input"
    v-model="model"
    :class="[
      'textarea w-full text-base',
      {
        'textarea-primary border bg-base-200 text-base-content': theme === 'default',
        'resize-none': !resize,
        'overflow-hidden': autoGrow,
      },
    ]"
    @input="validateInput"
    @blur="handleBlur"
  ></textarea>
</template>
<script lang="ts" setup>
  import { computed, nextTick, onMounted, ref, watch } from 'vue';
  import { useVModel } from '@vueuse/core';
  import { generateId } from '@core/utils';
  import type { Maybe } from '@shared/utils';
  import { useAutofillGuardAttrs, useAutofillPolicy } from '@shared/composables/useAutofillPolicy';

  export interface InputValidationSchema {
    pattern: string;
    title: string;
  }

  export type InputValueType = Maybe<string | number>;
  export type InputType = 'text' | 'textarea' | 'password' | 'number' | 'tel' | 'email' | 'alphanumeric';
  export interface MInputProps {
    type?: InputType;
    theme?: 'default' | 'custom';
    name?: string | null;
    autocomplete?: string | null;
    id?: string;
    modelValue?: InputValueType;
    readonly?: boolean;
    resize?: boolean;
    /**
     * `type="textarea"` only — start at the textarea's natural height and grow with the
     * content instead of occupying a fixed height. Pair with a `rows` attribute or a
     * `min-h-*` class to set the collapsed size.
     */
    autoGrow?: boolean;
    size?: 'xs' | 'sm' | 'md' | 'lg';
  }

  export interface Emits {
    (e: 'update:modelValue', modelValue: InputValueType): void;
    (e: 'blur'): void;
  }
  const emit = defineEmits<Emits>();

  const props = withDefaults(defineProps<MInputProps>(), {
    type: 'text',
    theme: 'default',
    name: null,
    autocomplete: null,
    id: undefined,
    placeholder: null,
    modelValue: '',
    resize: false,
    autoGrow: false,
    size: 'md',
  });

  // A default in withDefaults is evaluated once per page load, not per instance —
  // it would give every unnamed input the same id/name, which makes the browser
  // pool its remembered-entry suggestions across unrelated fields.
  const fallbackId = generateId();
  const resolvedId = computed(() => props.id || fallbackId);

  const autofillGuardAttrs = useAutofillGuardAttrs();
  const autofillPolicy = useAutofillPolicy();

  // Under the block policy, strip the semantic signals autofill extensions match on:
  // email/tel types become text (inputmode keeps the right mobile keyboard). Native
  // email-format validity checking is lost on blocked routes — studio forms rely on
  // schema validation instead.
  const resolvedType = computed(() => {
    if (props.type === 'alphanumeric') return 'text';
    if (autofillPolicy.value === 'block' && (props.type === 'email' || props.type === 'tel')) return 'text';
    return props.type;
  });
  const resolvedInputmode = computed(() =>
    autofillPolicy.value === 'block' && (props.type === 'email' || props.type === 'tel') ? props.type : undefined,
  );

  const sharedProps = computed(() => {
    const { readonly, autocomplete } = props;
    return {
      ...autofillGuardAttrs.value,
      id: resolvedId.value,
      readonly,
      name: nameProp.value,
      ...(autocomplete ? { autocomplete } : {}),
    };
  });

  const input = ref<HTMLInputElement>();
  // name is never used for form submission in this SPA; under the block policy a
  // per-mount random name denies extensions the name-matching signal even when a
  // semantic id (needed for the label's `for`) was passed explicitly.
  const nameProp = computed<string>(() =>
    autofillPolicy.value === 'block' ? fallbackId : props.name || resolvedId.value,
  );
  const model = useVModel(props, 'modelValue', emit);

  /**
   * Collapse to the natural height (driven by `rows`/`min-h-*`) and then grow to fit the
   * content. `scrollHeight` excludes the border, which `box-sizing: border-box` includes,
   * so add it back or the textarea ends up a couple of pixels short and re-scrolls.
   */
  const resizeToContent = () => {
    const el = input.value as HTMLTextAreaElement | undefined;
    if (!props.autoGrow || props.type !== 'textarea' || !el) return;

    el.style.height = 'auto';
    const { borderTopWidth, borderBottomWidth } = window.getComputedStyle(el);
    const borderY = (parseFloat(borderTopWidth) || 0) + (parseFloat(borderBottomWidth) || 0);
    el.style.height = `${el.scrollHeight + borderY}px`;
  };

  onMounted(() => nextTick(resizeToContent));
  watch(model, () => nextTick(resizeToContent));

  defineExpose({
    focus: () => input.value && (input.value as HTMLInputElement).focus(),
    valid: (): boolean => !!input.value && (input.value as HTMLInputElement).validity.valid,
    id: resolvedId,
  });

  const handleBlur = () => {
    validateInput();
    emit('blur');
  };

  const validateInput = () => {
    // First handle specific alphanumeric validation
    if (props.type === 'alphanumeric') {
      const alphanumericRegex = /^[a-zA-Z0-9\s]*$/;
      if (!alphanumericRegex.test(model.value as string)) {
        model.value = (model.value as string).replace(/[^a-zA-Z0-9\s]/g, '');
      }
      return;
    }

    // For all input types, remove tildes
    if (typeof model.value === 'string' && model.value.includes('~')) {
      model.value = model.value.replace(/~/g, '');
    }
  };
</script>

<style scoped lang="css">
  /*
    Using tailwind text-base to override daisyui .input and .textarea font-size
    16px prevents ios auto zooming
    https://css-tricks.com/16px-or-larger-text-prevents-ios-form-zoom/

    error style set in MFormControl.vue
  */

  /* start default or on white background */
  input,
  textarea,
  .bg-white input,
  .bg-white textarea {
    @apply border border-base-300 bg-base-100;
  }

  input[disabled],
  textarea[disabled],
  .bg-white input[disabled],
  .bg-white textarea[disabled] {
    @apply bg-base-300 text-opacity-50 placeholder-opacity-50;
  }
  /* end default or on white background */

  /* start on base-100 */
  .bg-base-100 input,
  .bg-base-100 textarea {
    --tw-bg-opacity: 1;
    background-color: rgb(255 255 255 / var(--tw-bg-opacity));
  }

  .bg-base-100 input[disabled],
  .bg-base-100 textarea[disabled] {
    @apply bg-base-300 text-opacity-50 placeholder-opacity-50;
  }
  /* end on base-100 */

  /* start on base-200 */
  /* end on base-200 */

  /* start on base-300 */
  .bg-base-300 input,
  .bg-base-300 textarea {
    --tw-bg-opacity: 1;
    background-color: rgb(255 255 255 / var(--tw-bg-opacity));
  }

  .bg-base-300 input[disabled],
  .bg-base-300 textarea[disabled] {
    @apply bg-base-200 text-opacity-50 placeholder-opacity-50;
  }
  /* end on base-300 */

  /* start on accent */
  .bg-accent input,
  .bg-accent textarea {
    @apply bg-base-300 text-primary;
  }

  .bg-accent input[disabled],
  .bg-accent textarea[disabled] {
    @apply bg-opacity-50 text-opacity-50 placeholder-opacity-50;
  }
  /* end on accent */
</style>
